import { Spinner } from "@kui-react/spinner";
import { Tooltip } from "@mui/material";
import FlatCard from "components/UI-lib/FlatCard";
import { motion } from "framer-motion";
import React from "react";
import { LaunchableFromList } from "server";

const MAX_VISUAL_HEIGHT = 100; // Max height for the tallest podium bar in pixels

// Data structured to make: Middle tallest, Left second, Right shortest

interface MetricData {
  label: string;
  value: number;
  valueTextColor: string;
  podiumColor: string;
  basePodiumColor: string;
  shadowEffect: string;
  launchable?: LaunchableFromList;
}
const metricsData: MetricData[] = [
  {
    label: "Launchable 1",
    value: 1230,
    podiumColor: "bg-sky-500",
    basePodiumColor: "bg-sky-700",
    valueTextColor: "text-sky-700",
    shadowEffect: "shadow-xl shadow-sky-500/40",
  },
  {
    label: "Launchable 2",
    value: 930,
    podiumColor: "bg-emerald-500",
    basePodiumColor: "bg-emerald-700",
    valueTextColor: "text-emerald-700",
    shadowEffect: "shadow-xl shadow-emerald-500/40",
  },
  {
    label: "Launchable 3",
    value: 800,
    podiumColor: "bg-purple-500",
    basePodiumColor: "bg-purple-700",
    valueTextColor: "text-purple-700",
    shadowEffect: "shadow-xl shadow-purple-500/40",
  },
];

interface PodiumItemProps {
  metricType: "views" | "deploys";
  metricData: MetricData;
  index: number;
  maxOverallValue: number;
}

const PodiumItem: React.FC<PodiumItemProps> = ({
  metricData,
  index,
  maxOverallValue,
  metricType,
}) => {
  const podiumHeight =
    maxOverallValue > 0
      ? (metricData.value / maxOverallValue) * MAX_VISUAL_HEIGHT
      : 0;

  return (
    <div className="flex flex-col items-center text-center w-32 mx-1">
      <motion.p
        className={`text-2xl md:text-3xl font-bold mb-1.5 ${metricData.valueTextColor}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: index * 0.2 + 0.5,
          duration: 0.5,
          type: "spring",
          stiffness: 100,
        }}
      >
        {metricData.value}
      </motion.p>
      <motion.p
        className="text-xs text-gray-500 mb-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: index * 0.2 + 0.6, duration: 0.5 }}
      >
        {metricType === "views" ? "Views" : "Deploys"}
      </motion.p>

      <motion.div
        className={`w-16 md:w-20 rounded-t-lg ${metricData.podiumColor} relative ${metricData.shadowEffect}`}
        style={{ minHeight: "1px" }} // Ensures div is rendered for animation
        initial={{ height: "1px", opacity: 0 }}
        animate={{
          height: `${Math.max(1, podiumHeight)}px`, // Ensure min height 1px
          opacity: 1,
        }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 15,
          delay: index * 0.15,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-white/10 rounded-t-lg"></div>
      </motion.div>

      <div
        className={`w-20 md:w-24 h-4 ${metricData.basePodiumColor} rounded-b-md shadow-md`}
      ></div>

      <motion.p
        className="mt-3 text-sm md:text-base font-semibold text-gray-700"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.2 + 0.7, duration: 0.4 }}
      >
        <Tooltip title={metricData.launchable?.name} placement="top">
          <span className="text-sm text-primary font-semibold">
            {metricData.label}
          </span>
        </Tooltip>
      </motion.p>
    </div>
  );
};

interface PodiumDisplayProps {
  metricType: "views" | "deploys";
  metrics: {
    label: string;
    value: number;
    launchable?: LaunchableFromList;
  }[];
  title: string;
  loading: boolean;
}

const PodiumDisplay: React.FC<PodiumDisplayProps> = ({
  metricType,
  metrics,
  title,
  loading,
}) => {
  const maxOverallValue = Math.max(...metrics.map((m) => m.value), 0);

  // Take the first three metricsData items and use their color properties
  const colorDefaults = metricsData.slice(0, 3);

  // Merge incoming metrics with color properties from samples
  const enrichedMetrics = metrics.slice(0, 3).map((metric, index) => ({
    ...metric,
    podiumColor: colorDefaults[index]?.podiumColor || "bg-gray-500",
    basePodiumColor: colorDefaults[index]?.basePodiumColor || "bg-gray-700",
    valueTextColor: colorDefaults[index]?.valueTextColor || "text-gray-700",
    shadowEffect:
      colorDefaults[index]?.shadowEffect || "shadow-xl shadow-gray-500/40",
  }));

  return (
    <FlatCard className="h-[325px]">
      <motion.h2
        className="text-xl font-bold text-primary mb-10 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {title}
      </motion.h2>

      {loading ? (
        <div className="flex flex-col h-full relative">
          <div className="absolute top-0 flex items-center justify-center w-full">
            <span className="text-sm text-primary font-semibold">
              Loading...
            </span>
          </div>
          <div className="flex justify-around items-end w-full opacity-10">
            {metricsData.map((metric, index) => (
              <PodiumItem
                key={metric.label}
                metricData={metric}
                metricType={metricType}
                index={index}
                maxOverallValue={1230}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex justify-around items-end w-full">
          {enrichedMetrics.map((metric, index) => (
            <PodiumItem
              key={metric.label}
              metricData={metric}
              metricType={metricType}
              index={index}
              maxOverallValue={maxOverallValue}
            />
          ))}
        </div>
      )}
    </FlatCard>
  );
};

export default PodiumDisplay;
